import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import type { Lead } from "./LeadModal";

/* ─── Source config (unified) ──────────────────────────────── */
export const SOURCE_CFG: Record<string, { label: string; icon: string; cls: string; bar: string }> = {
  public_website: { label: "Website",      icon: "ri-global-line",       cls: "bg-emerald-50 text-emerald-600 border-emerald-200",  bar: "bg-emerald-400" },
  ai_assistant:   { label: "AI Assistant", icon: "ri-robot-line",        cls: "bg-violet-50 text-violet-600 border-violet-200",     bar: "bg-violet-400" },
  contact_form:   { label: "Contact Form", icon: "ri-mail-line",         cls: "bg-sky-50 text-sky-600 border-sky-200",              bar: "bg-sky-400" },
  invitation:     { label: "Invitation",   icon: "ri-gift-line",         cls: "bg-rose-50 text-rose-600 border-rose-200",           bar: "bg-rose-400" },
  manual:         { label: "Manual",       icon: "ri-add-circle-line",   cls: "bg-slate-100 text-slate-500 border-slate-200",       bar: "bg-slate-400" },
  other:          { label: "Other",        icon: "ri-question-line",     cls: "bg-orange-50 text-orange-600 border-orange-200",     bar: "bg-orange-400" },
};

export const sourceCfg = (s: string | null) =>
  SOURCE_CFG[s ?? ""] ?? SOURCE_CFG["other"];

const fmtRelative = (d: string) => {
  const diff = Date.now() - new Date(d).getTime();
  const mins  = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days  = Math.floor(diff / 86400000);
  if (mins < 60)  return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  return `${days}d ago`;
};

interface Props {
  leads: Lead[];
}

export default function LeadsDashboard({ leads }: Props) {
  const navigate = useNavigate();

  /* ── Computed stats ── */
  const stats = useMemo(() => {
    const now  = new Date();
    const week = new Date(now.getTime() - 7 * 86400000);
    const month = new Date(now.getTime() - 30 * 86400000);

    const newLeads     = leads.filter((l) => l.status === "new");
    const thisWeek     = leads.filter((l) => new Date(l.created_at) >= week);
    const thisMonth    = leads.filter((l) => new Date(l.created_at) >= month);
    const booked       = leads.filter((l) => l.status === "booked");
    const convRate     = leads.length ? Math.round((booked.length / leads.length) * 100) : 0;

    return { newLeads, thisWeek, thisMonth, booked, convRate };
  }, [leads]);

  /* ── By source ── */
  const bySource = useMemo(() => {
    const map: Record<string, number> = {};
    leads.forEach((l) => {
      const key = l.source ?? "manual";
      map[key] = (map[key] ?? 0) + 1;
    });
    return Object.entries(map)
      .sort(([, a], [, b]) => b - a)
      .map(([source, count]) => ({ source, count, pct: leads.length ? Math.round((count / leads.length) * 100) : 0 }));
  }, [leads]);

  /* ── By event_type ── */
  const byEventType = useMemo(() => {
    const map: Record<string, number> = {};
    leads.forEach((l) => {
      if (!l.event_type) return;
      map[l.event_type] = (map[l.event_type] ?? 0) + 1;
    });
    return Object.entries(map)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 5);
  }, [leads]);

  /* ── Recent leads (last 5) ── */
  const recent = useMemo(() =>
    [...leads]
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
      .slice(0, 5),
    [leads],
  );

  const STATUS_BADGE: Record<string, string> = {
    new:       "bg-sky-50 text-sky-600 border-sky-200",
    contacted: "bg-amber-50 text-amber-600 border-amber-200",
    booked:    "bg-emerald-50 text-emerald-600 border-emerald-200",
    lost:      "bg-red-50 text-red-500 border-red-200",
  };

  return (
    <div className="space-y-5 mb-6">

      {/* ── KPI cards row ── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          {
            label: "New Leads",
            value: stats.newLeads.length,
            sub:   `${stats.thisWeek.length} this week`,
            icon:  "ri-user-received-2-line",
            iconCls: "bg-sky-50 text-sky-500",
            valCls: "text-sky-600",
          },
          {
            label: "This Month",
            value: stats.thisMonth.length,
            sub:   "last 30 days",
            icon:  "ri-calendar-line",
            iconCls: "bg-[#C8A96E]/10 text-[#9a7840]",
            valCls: "text-[#9a7840]",
          },
          {
            label: "Booked",
            value: stats.booked.length,
            sub:   "converted to client",
            icon:  "ri-checkbox-circle-line",
            iconCls: "bg-emerald-50 text-emerald-500",
            valCls: "text-emerald-600",
          },
          {
            label: "Conv. Rate",
            value: `${stats.convRate}%`,
            sub:   `${leads.length} total leads`,
            icon:  "ri-line-chart-line",
            iconCls: "bg-violet-50 text-violet-500",
            valCls: "text-violet-600",
          },
        ].map((card) => (
          <div key={card.label} className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-start justify-between mb-3">
              <div className={`w-8 h-8 flex items-center justify-center rounded-lg ${card.iconCls}`}>
                <i className={`${card.icon} text-sm`}></i>
              </div>
            </div>
            <p className={`text-2xl font-bold ${card.valCls}`}>{card.value}</p>
            <p className="text-slate-500 text-xs font-medium mt-0.5">{card.label}</p>
            <p className="text-slate-300 text-[10px] mt-0.5">{card.sub}</p>
          </div>
        ))}
      </div>

      {/* ── Bottom row: Source breakdown + Event types + Recent ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

        {/* Source breakdown */}
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="text-slate-600 text-xs font-medium uppercase tracking-wider mb-4">Leads by Source</h3>
          {bySource.length === 0 ? (
            <p className="text-slate-300 text-sm text-center py-6">No data yet</p>
          ) : (
            <div className="space-y-3">
              {bySource.map(({ source, count, pct }) => {
                const cfg = sourceCfg(source);
                return (
                  <div key={source}>
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-2">
                        <div className={`w-6 h-6 flex items-center justify-center rounded-md border text-[10px] ${cfg.cls}`}>
                          <i className={cfg.icon}></i>
                        </div>
                        <span className="text-slate-600 text-xs font-medium">{cfg.label}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-slate-400 text-xs">{count}</span>
                        <span className="text-slate-300 text-[10px] w-6 text-right">{pct}%</span>
                      </div>
                    </div>
                    <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-500 ${cfg.bar}`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Top event types */}
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="text-slate-600 text-xs font-medium uppercase tracking-wider mb-4">Top Event Types</h3>
          {byEventType.length === 0 ? (
            <p className="text-slate-300 text-sm text-center py-6">No event data yet</p>
          ) : (
            <div className="space-y-2">
              {byEventType.map(([type, count], idx) => {
                const pct = leads.length ? Math.round((count / leads.length) * 100) : 0;
                const colors = [
                  "text-[#9a7840] bg-[#C8A96E]/10",
                  "text-sky-600 bg-sky-50",
                  "text-emerald-600 bg-emerald-50",
                  "text-violet-600 bg-violet-50",
                  "text-rose-600 bg-rose-50",
                ];
                return (
                  <div key={type} className="flex items-center gap-3 py-1.5">
                    <span className={`w-5 h-5 flex items-center justify-center rounded-md text-[10px] font-bold ${colors[idx % colors.length]}`}>
                      {idx + 1}
                    </span>
                    <span className="text-slate-600 text-xs flex-1 truncate">{type}</span>
                    <span className="text-slate-400 text-xs shrink-0">{count}</span>
                    <div className="w-14 h-1.5 bg-slate-100 rounded-full overflow-hidden shrink-0">
                      <div className="h-full bg-[#C8A96E] rounded-full" style={{ width: `${Math.min(pct * 2, 100)}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Recent leads */}
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-slate-600 text-xs font-medium uppercase tracking-wider">Recent Leads</h3>
            <span className="text-slate-300 text-[10px]">last {recent.length}</span>
          </div>
          {recent.length === 0 ? (
            <p className="text-slate-300 text-sm text-center py-6">No leads yet</p>
          ) : (
            <div className="space-y-2.5">
              {recent.map((lead) => {
                const badge = STATUS_BADGE[lead.status] ?? "bg-slate-100 text-slate-500 border-slate-200";
                const sc = sourceCfg(lead.source ?? null);
                return (
                  <button
                    key={lead.id}
                    onClick={() => navigate(`/admin/leads/${lead.id}`)}
                    className="w-full flex items-center gap-2.5 text-left group cursor-pointer"
                  >
                    <div className="w-7 h-7 flex items-center justify-center rounded-full bg-[#C8A96E]/10 text-[#9a7840] text-xs font-bold shrink-0">
                      {lead.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-slate-700 text-xs font-medium truncate group-hover:text-[#9a7840] transition-colors">{lead.name}</p>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <i className={`${sc.icon} text-[9px] text-slate-400`}></i>
                        <span className="text-slate-300 text-[10px]">{fmtRelative(lead.created_at)}</span>
                      </div>
                    </div>
                    <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-medium border shrink-0 ${badge}`}>
                      {lead.status}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}